import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { MusicalWorkCreateStore } from "../../store";
import { runInAction } from "mobx";
import { InputString } from "../../../common/controls";
import { InputReference } from "../../../common/controls";
import { InputStringList } from "../../../common/controls";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { WorkAuthorCreate } from "../../../workAuthor/components/create/WorkAuthorCreate";
import { WorkAuthorCreateStore } from "../../../workAuthor/store";
import { GridColumn } from "../../../common/types";
import { InputText } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { InputReferenceSet } from "../../../common/controls";
import { CreateEntityDialog } from "../../../common/controls";
import { InterpreterCreate } from "../../../interpreter/components/create/InterpreterCreate";
import { InterpreterCreateStore } from "../../../interpreter/store";
import { TerritoryCreate } from "../../../territory/components/create/TerritoryCreate";
import { TerritoryCreateStore } from "../../../territory/store";
import { MusicalWorkIswcCustomField } from "../custom/MusicalWorkIswcCustomField";
import { FileCRUDType } from "../../../common/types";
import { LoadFilesCRUDCompact } from "../../../common/controls";
import { PhonogramCreate } from "../../../phonogram/components/create/PhonogramCreate";
import { PhonogramCreateStore } from "../../../phonogram/store";
import { InputHtmlString } from "../../../common/controls";

export type MusicalWorkCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  titleSelectedValue?: { displayId: string; id: string } | null;
  onTitleChange?: (value: string) => void;
  title: string;
  titleError?: string;
  musicalGenreSelectedValue?: { displayId: string; id: string } | null;
  onMusicalGenreSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  onMusicalGenreChange?: (value: string) => void;
  musicalGenreOptions?: { displayId: string; id: string }[];
  musicalGenreError?: string;
  alternativeTitlesSelectedValue?: { displayId: string; id: string } | null;
  onAlternativeTitlesChange?: (value: string[]) => void;
  alternativeTitles: string[];
  alternativeTitlesError?: string;
  authorsOnRestore: (id: string) => void;
  authorsOnCommitChanges: (changes: GridChangeSet) => void;
  authorsValidateChanges: (changes: GridChangeSet) => boolean;
  authorsRows: CrudRow[];
  workAuthorCreateStore: WorkAuthorCreateStore;
  authorsError?: string;
  authorsVisibleColumns: GridColumn[];
  notesSelectedValue?: { displayId: string; id: string } | null;
  onNotesChange?: (value: string) => void;
  notes: string;
  notesError?: string;
  performersSelectedValues: { displayId: string; id: string }[];
  onPerformersSelected?: (value: { displayId: string; id: string }[]) => void;
  onPerformersChange?: (value: string) => void;
  performersOptions?: { displayId: string; id: string }[];
  performersError?: string;
  cmsIdSelectedValue?: { displayId: string; id: string } | null;
  onCmsIdChange?: (value: string) => void;
  cmsId: string;
  cmsIdError?: string;
  territoriesSelectedValues: { displayId: string; id: string }[];
  onTerritoriesSelected?: (value: { displayId: string; id: string }[]) => void;
  onTerritoriesChange?: (value: string) => void;
  territoriesOptions?: { displayId: string; id: string }[];
  territoriesError?: string;
  iswcSelectedValue?: { displayId: string; id: string } | null;
  onIswcChange?: (value: string) => void;
  iswc: string;
  iswcError?: string;
  scoreOnRestoreFile: (id: string) => void;
  scoreOnLoadFile: (file: any) => void;
  scoreOnCommitFile: (changes: GridChangeSet) => void;
  scoreFiles: FileCRUDType[];
  scoreError?: string;
  phonogramsOnRestore: (id: string) => void;
  phonogramsOnCommitChanges: (changes: GridChangeSet) => void;
  phonogramsValidateChanges: (changes: GridChangeSet) => boolean;
  phonogramsRows: CrudRow[];
  phonogramCreateStore: PhonogramCreateStore;
  phonogramsError?: string;
  phonogramsVisibleColumns: GridColumn[];
  originalLyricsSelectedValue?: { displayId: string; id: string } | null;
  onOriginalLyricsChange?: (value: string) => void;
  originalLyrics: string;
  originalLyricsError?: string;
  contractObservationSelectedValue?: { displayId: string; id: string } | null;
  onContractObservationChange?: (value: string) => void;
  contractObservation: string;
  contractObservationError?: string;
  dummyCRUDRestoredObservable: number;
  musicalWorkCreateStore: MusicalWorkCreateStore;
};

export const MusicalWorkCreateForm: React.FC<MusicalWorkCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      title = "",
      titleError,
      onTitleChange = () => {},
      onMusicalGenreSelected = () => {},
      musicalGenreSelectedValue = null,
      musicalGenreOptions = [],
      musicalGenreError,
      onMusicalGenreChange = () => {},
      alternativeTitles = [],
      alternativeTitlesError,
      onAlternativeTitlesChange = () => {},
      authorsOnRestore,
      authorsOnCommitChanges,
      authorsValidateChanges,
      authorsRows,
      workAuthorCreateStore,
      authorsError,
      authorsVisibleColumns,
      notes = "",
      notesError,
      onNotesChange = () => {},
      onPerformersSelected = () => {},
      performersSelectedValues,
      performersOptions = [],
      performersError,
      onPerformersChange = () => {},
      cmsId = "",
      cmsIdError,
      onCmsIdChange = () => {},
      onTerritoriesSelected = () => {},
      territoriesSelectedValues,
      territoriesOptions = [],
      territoriesError,
      onTerritoriesChange = () => {},
      iswc = "",
      iswcError,
      onIswcChange = () => {},
      scoreOnRestoreFile,
      scoreOnLoadFile,
      scoreOnCommitFile,
      scoreFiles,
      scoreError,
      phonogramsOnRestore,
      phonogramsOnCommitChanges,
      phonogramsValidateChanges,
      phonogramsRows,
      phonogramCreateStore,
      phonogramsError,
      phonogramsVisibleColumns,
      originalLyrics = "",
      originalLyricsError,
      onOriginalLyricsChange = () => {},
      contractObservation = "",
      contractObservationError,
      onContractObservationChange = () => {},
      dummyCRUDRestoredObservable,
      musicalWorkCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      if (workAuthorCreateStore.status === "uninitialized") {
        workAuthorCreateStore.initialize();
      }

      if (phonogramCreateStore.status === "uninitialized") {
        phonogramCreateStore.initialize();
      }

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormRecordProvider
              values={{
                title,
                musicalGenre: musicalGenreSelectedValue,
                alternativeTitles,
                notes,
                cmsId,
                iswc,
                originalLyrics,
                contractObservation,
              }}
            >
              <FormExtras
                entity="MusicalWork"
                mode="create"
                values={{
                  title,
                  musicalGenre: musicalGenreSelectedValue,
                  alternativeTitles,
                  notes,
                  cmsId,
                  iswc,
                  originalLyrics,
                  contractObservation,
                }}
                options={{ musicalGenre: musicalGenreOptions }}
                onPatch={(campo: string, valor: any) => {
                  switch (campo) {
                    case "title":
                      onTitleChange?.(valor);
                      break;
                    case "musicalGenre":
                      onMusicalGenreSelected?.(valor);
                      break;
                    case "alternativeTitles":
                      onAlternativeTitlesChange?.(valor);
                      break;
                    case "notes":
                      onNotesChange?.(valor);
                      break;
                    case "cmsId":
                      onCmsIdChange?.(valor);
                      break;
                    case "iswc":
                      onIswcChange?.(valor);
                      break;
                    case "originalLyrics":
                      onOriginalLyricsChange?.(valor);
                      break;
                    case "contractObservation":
                      onContractObservationChange?.(valor);
                      break;
                  }
                }}
              />

              <GroupContainer
                id="workInfo"
                errorText={musicalWorkCreateStore.getCardErrors("workInfo")}
                title={langStore.groupMusicalWorkWorkInfoLabel}
                collapsible={true}
                expanded={musicalWorkCreateStore.getCardState("workInfo")}
                toggleExpand={() =>
                  musicalWorkCreateStore.toggleCardState("workInfo")
                }
              >
                <FormControl fullWidth>
                  <InputString
                    autoFocus
                    value={title}
                    error={Boolean(titleError)}
                    warning={
                      !titleError &&
                      Boolean(musicalWorkCreateStore.warnings["title"])
                    }
                    helperText={
                      titleError ||
                      musicalWorkCreateStore.warnings["title"]?.message
                    }
                    label={langStore.MusicalWorkTitleLabel}
                    onChange={(value: string) => onTitleChange(value)}
                    description={langStore.MusicalWorkTitleDescription}
                  />
                  <InputReference
                    id="musicalGenre"
                    label={langStore.MusicalWorkMusicalGenreLabel}
                    error={Boolean(musicalGenreError)}
                    helperText={
                      musicalGenreError ||
                      musicalWorkCreateStore.warnings["musicalGenre"]?.message
                    }
                    selectedValue={musicalGenreSelectedValue}
                    onChange={(value: string) => onMusicalGenreChange(value)}
                    onSelect={(
                      value: { displayId: string; id: string } | null
                    ) => onMusicalGenreSelected(value)}
                    options={musicalGenreOptions}
                    description={langStore.MusicalWorkMusicalGenreDescription}
                  />
                  <InputStringList
                    value={alternativeTitles}
                    error={Boolean(alternativeTitlesError)}
                    helperText={
                      alternativeTitlesError ||
                      musicalWorkCreateStore.warnings["alternativeTitles"]
                        ?.message
                    }
                    label={langStore.MusicalWorkAlternativeTitlesLabel}
                    description={
                      langStore.MusicalWorkAlternativeTitlesDescription
                    }
                    onChange={(value: string[]) =>
                      onAlternativeTitlesChange(value)
                    }
                  />
                  <ItemsCRUDCompact
                    label={langStore.MusicalWorkAuthorsLabel}
                    description={langStore.MusicalWorkAuthorsDescription}
                    showLabel={true}
                    rows={authorsRows}
                    footerData={musicalWorkCreateStore.authorsFooter}
                    columns={authorsVisibleColumns}
                    popupComponent={(row: any) => {
                      if (
                        row &&
                        row.id &&
                        !(
                          typeof row.id === "string" &&
                          row.id.startsWith("auto-")
                        )
                      ) {
                        runInAction(() =>
                          musicalWorkCreateStore.authorsLoadRow(row)
                        );
                      }
                      return (
                        <WorkAuthorCreate
                          workAuthorCreateStore={workAuthorCreateStore}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      );
                    }}
                    popupMaxWidth="xl"
                    onRestore={(id: string) => authorsOnRestore(id)}
                    onCommitChanges={(changes: GridChangeSet) =>
                      authorsOnCommitChanges(changes)
                    }
                    validate={(changes: GridChangeSet) =>
                      authorsValidateChanges(changes)
                    }
                    dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                    error={authorsError}
                  />

                  <InputText
                    value={notes}
                    error={Boolean(notesError)}
                    warning={
                      !notesError &&
                      Boolean(musicalWorkCreateStore.warnings["notes"])
                    }
                    helperText={
                      notesError ||
                      musicalWorkCreateStore.warnings["notes"]?.message
                    }
                    label={langStore.MusicalWorkNotesLabel}
                    onChange={(value: string) => onNotesChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.MusicalWorkNotesDescription}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="workRegistration"
                errorText={musicalWorkCreateStore.getCardErrors(
                  "workRegistration"
                )}
                title={langStore.groupMusicalWorkWorkRegistrationLabel}
                collapsible={true}
                expanded={musicalWorkCreateStore.getCardState(
                  "workRegistration"
                )}
                toggleExpand={() =>
                  musicalWorkCreateStore.toggleCardState("workRegistration")
                }
              >
                <FormControl fullWidth>
                  <InputReferenceSet
                    id="performers"
                    label={langStore.MusicalWorkPerformersLabel}
                    error={Boolean(performersError)}
                    helperText={
                      performersError ||
                      musicalWorkCreateStore.warnings["performers"]?.message
                    }
                    selectedValues={performersSelectedValues}
                    onChange={(value: string) => onPerformersChange(value)}
                    onSelect={(value: { displayId: string; id: string }[]) =>
                      onPerformersSelected(value)
                    }
                    options={performersOptions}
                    description={langStore.MusicalWorkPerformersDescription}
                    nullText={langStore.optionalFieldHint}
                    canCreate={rootStore.authStore.hasPermissions(
                      "interpreter_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new InterpreterCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <InterpreterCreate
                            interpreterCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewInterpreter"
                        cancelMethod="cancelNewInterpreter"
                      />
                    )}
                  />

                  <InputString
                    value={cmsId}
                    error={Boolean(cmsIdError)}
                    warning={
                      !cmsIdError &&
                      Boolean(musicalWorkCreateStore.warnings["cmsId"])
                    }
                    helperText={
                      cmsIdError ||
                      musicalWorkCreateStore.warnings["cmsId"]?.message
                    }
                    label={langStore.MusicalWorkCmsIdLabel}
                    onChange={(value: string) => onCmsIdChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.MusicalWorkCmsIdDescription}
                  />
                  <InputReferenceSet
                    id="territories"
                    label={langStore.MusicalWorkTerritoriesLabel}
                    error={Boolean(territoriesError)}
                    helperText={
                      territoriesError ||
                      musicalWorkCreateStore.warnings["territories"]?.message
                    }
                    selectedValues={territoriesSelectedValues}
                    onChange={(value: string) => onTerritoriesChange(value)}
                    onSelect={(value: { displayId: string; id: string }[]) =>
                      onTerritoriesSelected(value)
                    }
                    options={territoriesOptions}
                    description={langStore.MusicalWorkTerritoriesDescription}
                    pinnedDisplayIds={["Todo el mundo"]}
                    canCreate={rootStore.authStore.hasPermissions(
                      "territory_create"
                    )}
                    renderCreateDialog={(dialogProps) => (
                      <CreateEntityDialog
                        {...dialogProps}
                        createStore={(rootStore) =>
                          new TerritoryCreateStore(rootStore)
                        }
                        renderForm={(store) => (
                          <TerritoryCreate
                            territoryCreateStore={store}
                            showToolbox={false}
                            showSaveFooter={false}
                          />
                        )}
                        saveMethod="saveNewTerritory"
                        cancelMethod="cancelNewTerritory"
                      />
                    )}
                  />
                  <MusicalWorkIswcCustomField
                    value={iswc}
                    error={Boolean(iswcError)}
                    helperText={
                      iswcError ||
                      musicalWorkCreateStore.warnings["iswc"]?.message
                    }
                    {...{ optional: true }}
                    label={langStore.MusicalWorkIswcLabel}
                    description={langStore.MusicalWorkIswcDescription}
                    onChange={(value: string) => onIswcChange(value)}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="workContent"
                errorText={musicalWorkCreateStore.getCardErrors("workContent")}
                title={langStore.groupMusicalWorkWorkContentLabel}
                collapsible={true}
                expanded={musicalWorkCreateStore.getCardState("workContent")}
                toggleExpand={() =>
                  musicalWorkCreateStore.toggleCardState("workContent")
                }
              >
                <FormControl fullWidth>
                  <LoadFilesCRUDCompact
                    rows={scoreFiles}
                    maxRows={1}
                    acceptFileTypes=".pdf"
                    showAddCommand={true}
                    showDeleteCommand={true}
                    label={langStore.MusicalWorkScoreLabel}
                    description={langStore.MusicalWorkScoreDescription}
                    showLabel={true}
                    onLoadFile={(file: File) => scoreOnLoadFile(file)}
                    columns={[
                      { name: "mimetype", title: langStore.fileTypeLabel },
                      { name: "name", title: langStore.fileNameLabel },
                      { name: "formattedSize", title: langStore.fileSizeLabel },
                    ]}
                    onRestore={(id: string) => scoreOnRestoreFile(id)}
                    onCommitChanges={(changes: GridChangeSet) =>
                      scoreOnCommitFile(changes)
                    }
                    dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                    error={scoreError}
                  />
                  <ItemsCRUDCompact
                    label={langStore.MusicalWorkPhonogramsLabel}
                    description={langStore.MusicalWorkPhonogramsDescription}
                    showLabel={true}
                    rows={phonogramsRows}
                    columns={phonogramsVisibleColumns}
                    popupComponent={(row: any) => {
                      if (
                        row &&
                        row.id &&
                        !(
                          typeof row.id === "string" &&
                          row.id.startsWith("auto-")
                        )
                      ) {
                        runInAction(() =>
                          musicalWorkCreateStore.phonogramsLoadRow(row)
                        );
                      }
                      return (
                        <PhonogramCreate
                          phonogramCreateStore={phonogramCreateStore}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      );
                    }}
                    popupMaxWidth="xl"
                    onRestore={(id: string) => phonogramsOnRestore(id)}
                    onCommitChanges={(changes: GridChangeSet) =>
                      phonogramsOnCommitChanges(changes)
                    }
                    validate={(changes: GridChangeSet) =>
                      phonogramsValidateChanges(changes)
                    }
                    dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                    error={phonogramsError}
                  />

                  <InputText
                    value={originalLyrics}
                    error={Boolean(originalLyricsError)}
                    warning={
                      !originalLyricsError &&
                      Boolean(musicalWorkCreateStore.warnings["originalLyrics"])
                    }
                    helperText={
                      originalLyricsError ||
                      musicalWorkCreateStore.warnings["originalLyrics"]?.message
                    }
                    label={langStore.MusicalWorkOriginalLyricsLabel}
                    onChange={(value: string) => onOriginalLyricsChange(value)}
                    nullText={langStore.optionalFieldHint}
                    description={langStore.MusicalWorkOriginalLyricsDescription}
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="contracts"
                errorText={musicalWorkCreateStore.getCardErrors("contracts")}
                title={langStore.groupMusicalWorkContractsLabel}
                collapsible={true}
                expanded={musicalWorkCreateStore.getCardState("contracts")}
                toggleExpand={() =>
                  musicalWorkCreateStore.toggleCardState("contracts")
                }
              >
                <FormControl fullWidth>
                  <InputHtmlString
                    value={contractObservation}
                    error={Boolean(contractObservationError)}
                    helperText={
                      contractObservationError ||
                      musicalWorkCreateStore.warnings["contractObservation"]
                        ?.message
                    }
                    label={langStore.MusicalWorkContractObservationLabel}
                    onChange={(value: string) =>
                      onContractObservationChange(value)
                    }
                    fragment={true}
                    description={
                      langStore.MusicalWorkContractObservationDescription
                    }
                  />
                </FormControl>
              </GroupContainer>

              <GroupContainer
                id="systemDates"
                errorText={musicalWorkCreateStore.getCardErrors("systemDates")}
                title={langStore.groupMusicalWorkSystemDatesLabel}
                collapsible={true}
                expanded={musicalWorkCreateStore.getCardState("systemDates")}
                toggleExpand={() =>
                  musicalWorkCreateStore.toggleCardState("systemDates")
                }
              >
                <FormControl fullWidth></FormControl>
              </GroupContainer>
            </FormRecordProvider>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
